import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { addWarning } from 'actions/Error';
import {
  getInvoiceFullyReturned,
  getReturnDocument,
} from 'reducers/returnProducts';
import { resetReturnDocument } from 'actions/returnProducts';
import { useConfirmation } from 'components/Confirmation';
import { closeModalPage } from 'actions/modalPage';
import { cancelOrderAction } from 'actions/sales';

export default function useOrderCancellation() {
  const dispatch = useDispatch();
  const returnDocument = useSelector(getReturnDocument);
  const { t } = useTranslation(['return', 'common']);
  const confirm = useConfirmation();
  const invoiceFullyReturned = useSelector(getInvoiceFullyReturned);

  const handleCancel = useCallback(async () => {
    dispatch(resetReturnDocument());

    switch (returnDocument.type) {
      case 'ORDER':
        await confirm({
          title: t('alerts.orderNotSupported.title', { ns: 'return' }),
          body: t('alerts.orderNotSupported.body', { ns: 'return' }),
          confirmText: t('yes', { ns: 'common' }),
          cancelText: t('close', { ns: 'common' }),
        });
        dispatch(closeModalPage());
        dispatch(cancelOrderAction(returnDocument));
        break;

      default:
        throw new Error('This should never happen');
    }
  }, [confirm, dispatch, returnDocument, t]);

  const checkDocumentType = useCallback(async () => {
    if (!returnDocument) return;

    const { type, invoiceState } = returnDocument;

    if (type === 'ORDER') {
      switch (invoiceState) {
        case 'FULFILLED':
          dispatch(addWarning(t('alerts.alreadyFulfilled', { ns: 'return' })));
          return;
        case 'CANCELLED':
          dispatch(addWarning(t('alerts.alreadyCancelled', { ns: 'return' })));
          return;
        default:
          try {
            await handleCancel();
          } catch (error) {
            dispatch(resetReturnDocument());
          }
          return;
      }
    }

    if (type === 'PREPAYMENT') {
      dispatch(
        addWarning(t('alerts.prepaymentNotSupported', { ns: 'return' })),
      );
      dispatch(resetReturnDocument());
      return;
    }

    if (invoiceFullyReturned) {
      dispatch(addWarning(t('alerts.alreadyReturned', { ns: 'return' })));
      dispatch(resetReturnDocument());
    }
  }, [dispatch, handleCancel, invoiceFullyReturned, returnDocument, t]);

  useEffect(() => {
    checkDocumentType();
  }, [checkDocumentType]);
}
